/**
 * Shared hook for the ManuallyAddPlayer integration.
 *
 * Encapsulates feature flag check, open/close state, theme resolution,
 * and the renderOption helper so that every consumer (GlobalSearchModal,
 * AddToListSheet, the shadow-team add-player modal) gets the same behaviour
 * without duplication.
 * @module hooks/useManuallyAddPlayer
 */

import { type ReactNode, useCallback, useMemo, useState } from "react";

import ManuallyAddPlayerOption from "@/components/molecules/ManuallyAddPlayerOption";
import type { ManuallyAddPlayerVariant } from "@/components/molecules/ManuallyAddPlayerOption";
import { POSTHOG_FLAGS } from "@/config/featureFlags";
import usePostHogFeatureFlag from "@/hooks/usePostHogFeatureFlag";
import { useTheme } from "@/providers/ThemeProvider";

/** Return type of useManuallyAddPlayer */
interface UseManuallyAddPlayerReturn {
  /** Whether the MANUALLY_ADD_PLAYER feature flag is enabled */
  readonly isEnabled: boolean;
  /** Whether the ManuallyAddPlayerModal is currently open */
  readonly isModalOpen: boolean;
  /** Opens the ManuallyAddPlayerModal */
  readonly handleOpen: () => void;
  /** Closes the ManuallyAddPlayerModal */
  readonly handleClose: () => void;
  /** Returns a ManuallyAddPlayerOption node (or null when disabled) */
  readonly renderOption: (
    subtitle: string,
    variant?: ManuallyAddPlayerVariant
  ) => ReactNode;
}

/**
 * Shared hook that owns ManuallyAddPlayer feature flag, modal state, and option rendering.
 * @returns Feature flag state, modal open/close handlers, and a renderOption helper
 */
const useManuallyAddPlayer = (): UseManuallyAddPlayerReturn => {
  const isEnabled =
    usePostHogFeatureFlag(POSTHOG_FLAGS.MANUALLY_ADD_PLAYER) === true;
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleOpen = useCallback(() => setIsModalOpen(true), []);
  const handleClose = useCallback(() => setIsModalOpen(false), []);

  const renderOption = useCallback(
    (subtitle: string, variant?: ManuallyAddPlayerVariant): ReactNode =>
      isEnabled ? (
        <ManuallyAddPlayerOption
          subtitle={subtitle}
          onPress={handleOpen}
          isDark={isDark}
          variant={variant}
        />
      ) : null,
    [isEnabled, handleOpen, isDark]
  );

  /*
   * Memoized so the returned object is referentially stable. Consumers put
   * this object in `useMemo` dependency arrays; a fresh object per render
   * made those memos recompute every render, which handed brand-new element
   * references to `React.memo` boundaries downstream and defeated them
   * (SE-7385).
   */
  return useMemo(
    () => ({ isEnabled, isModalOpen, handleOpen, handleClose, renderOption }),
    [isEnabled, isModalOpen, handleOpen, handleClose, renderOption]
  );
};

export default useManuallyAddPlayer;
